/**
 * RoosterJS editor plugin that forces pasted content to be inserted as plain
 * text, stripping any HTML markup carried over from the clipboard.
 *
 * Why this exists:
 * The escalation rich-text fields (Issue Description, Desired Outcome, Business
 * Impact) are served through an Azure Front Door that has a Web Application
 * Firewall in front of the API. When users paste formatted content copied from
 * Word, Outlook, or web pages, the editor preserves the inline HTML markup
 * (e.g. <span style="font-size:..."></span>, <strong>, <b>). That markup -
 * particularly style= attributes and HTML tags - trips the WAF's OWASP/DRS XSS
 * rules, which return a 403 and cause escalation submission to fail silently
 * for the user. It also produced visible artifacts such as pasted text being
 * rendered in a larger font.
 *
 * Inserting paste content as plain text removes the markup before it can reach
 * the request body, fixing both the submission failure and the unwanted
 * inherited formatting. Typing and toolbar-applied formatting are unaffected -
 * only paste is normalized.
 */
export class PlainTextPastePlugin {
    /**
     * Returns the unique name of this plugin.
     */
    getName() {
        return "PlainTextPaste";
    }
    /**
     * Initializes the plugin. No setup is required.
     */
    initialize() {
        // Intentionally empty - this plugin is stateless.
    }
    /**
     * Disposes the plugin. No resources to release.
     */
    dispose() {
        // Intentionally empty - this plugin holds no resources.
    }
    /**
     * Handles editor events. On a paste, the formatted fragment that RoosterJS is
     * about to insert is replaced with its plain-text equivalent.
     */
    onPluginEvent(event) {
        const beforePaste = asBeforePasteEvent(event);
        if (!beforePaste) {
            return;
        }
        const { fragment, clipboardData } = beforePaste;
        // Prefer the clipboard's plain-text representation; fall back to extracting
        // text from the fragment (preserving line and block boundaries) when the
        // clipboard plain text is unavailable.
        const plainText = clipboardData.text || extractPlainText(fragment) || "";
        // Replace the formatted paste content with its plain-text equivalent. When
        // there is no text - e.g. an image- or file-only paste - this clears the
        // fragment so no HTML (such as a base64 <img> that would also trip the WAF
        // and bloat the payload) is inserted into these plain-text escalation
        // fields. Images should be added via the Attachments control instead.
        replaceFragmentWithPlainText(fragment, plainText);
    }
}
/**
 * Structurally narrows a plugin event to a BeforePasteEvent.
 *
 * The RoosterJS PluginEventType is declared as a `const enum`, which cannot be
 * referenced as a runtime value under this project's `isolatedModules` /
 * ts-loader `transpileOnly` build. The BeforePaste event is uniquely identified
 * by carrying both a DocumentFragment and clipboard data, so it is detected by
 * shape rather than by comparing the enum value.
 */
function asBeforePasteEvent(event) {
    var _a;
    const candidate = event;
    // Use nodeType rather than `instanceof DocumentFragment`: the fragment can be
    // created by the editor's owning document (e.g. inside an iframe), and
    // `instanceof` is realm-specific so it would miss cross-realm fragments.
    if (((_a = candidate.fragment) === null || _a === void 0 ? void 0 : _a.nodeType) === Node.DOCUMENT_FRAGMENT_NODE &&
        candidate.clipboardData != null) {
        return candidate;
    }
    return undefined;
}
/**
 * Block-level tags whose boundaries should become line breaks when extracting
 * plain text from a paste fragment.
 */
const BLOCK_LEVEL_TAGS = new Set([
    "ARTICLE",
    "BLOCKQUOTE",
    "DIV",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "LI",
    "OL",
    "P",
    "PRE",
    "SECTION",
    "TABLE",
    "TR",
    "UL",
]);
/**
 * Extracts plain text from a paste fragment while preserving line breaks.
 *
 * This is only used as a fallback when the clipboard provides no plain-text
 * representation. Using `fragment.textContent` directly would concatenate the
 * text of adjacent block elements (e.g. paragraphs) with no separator, so
 * <br> elements and block-level boundaries are translated into newlines here.
 */
function extractPlainText(fragment) {
    let text = "";
    const ensureNewline = () => {
        if (text.length > 0 && !text.endsWith("\n")) {
            text += "\n";
        }
    };
    const walk = (node) => {
        var _a;
        if (node.nodeType === Node.TEXT_NODE) {
            text += (_a = node.textContent) !== null && _a !== void 0 ? _a : "";
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            node.childNodes.forEach(walk);
            return;
        }
        const element = node;
        if (element.tagName === "BR") {
            ensureNewline();
            return;
        }
        const isBlock = BLOCK_LEVEL_TAGS.has(element.tagName);
        if (isBlock) {
            ensureNewline();
        }
        element.childNodes.forEach(walk);
        if (isBlock) {
            ensureNewline();
        }
    };
    fragment.childNodes.forEach(walk);
    return text.replace(/\n{3,}/g, "\n\n").trimEnd();
}
/**
 * Replaces the contents of the paste fragment with plain text, preserving line
 * breaks as <br> elements.
 */
function replaceFragmentWithPlainText(fragment, text) {
    var _a;
    const ownerDocument = (_a = fragment.ownerDocument) !== null && _a !== void 0 ? _a : document;
    // Remove any existing (formatted) nodes.
    while (fragment.firstChild) {
        fragment.removeChild(fragment.firstChild);
    }
    const lines = text.split(/\r\n|\r|\n/);
    lines.forEach((line, index) => {
        if (index > 0) {
            fragment.appendChild(ownerDocument.createElement("br"));
        }
        if (line.length > 0) {
            fragment.appendChild(ownerDocument.createTextNode(line));
        }
    });
}
